import React, { useState } from 'react'
import {
  ContentWrapper,
  SmallValue,
  Value,
  Item,
  Icon,
  ItemActions,
} from '../styles'
import { parseISO } from 'date-fns'
import { safeFormat } from '@/v2/utils/convert'
import { useTheme } from '@mui/material/styles'
import Typography from '@/v2/components/common/Typography'
import Button from '@/v2/components/common/Button'
import {
  useAddCheckEligibility,
  useRemoveEligibility,
} from '@/v2/hooks/insurance/useEligibilities'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import useModals from '@/v2/hooks/useModals'
import SVGInline from '@/v2/components/common/SVGInline'
import ReloadIcon from '#/assets/images/v2/icons/reload.svg?raw'
import MoreIcon from '@mui/icons-material/MoreVert'
import IconMenu from '@/v2/components/common/IconMenu'
import Dialog from '@mui/material/Dialog'
import { Grid } from '@mui/material'

const EligibilityInfo = ({ eligibility, patient, onClaimClick }) => {
  const theme = useTheme()
  const { createdAt, provider, insurance, status } = eligibility
  const [loading, setLoading] = useState(false)
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false)
  const { addCheckEligibility } = useAddCheckEligibility(patient.id)
  const { removeEligibility } = useRemoveEligibility(
    insurance.id,
    eligibility.id,
    patient.id
  )
  const { onOpenSnackbar } = useSnackbarContext()
  const { goTo } = useModals()

  const handleConfirmDelete = async () => {
    const { error } = await removeEligibility()
    if (!error) {
      onOpenSnackbar('Eligibility removed', 'success')
    }
    setOpenDeleteConfirmation(false)
  }

  const options = [
    {
      label: 'View',
      onClick: () =>
        goTo(`/v2/patient/${patient.id}/eligibility/show`, {
          modalType: 'show',
          eligibility,
          patient,
          onClaimClick,
        }),
    },
    {
      label: 'Remove',
      onClick: () => setOpenDeleteConfirmation(true),
    },
  ]

  const handleCheckEligibility = async () => {
    setLoading(true)
    const body = {
      insuranceId: insurance.id,
      eligibility: {
        providerAttributes: {
          firstName: provider?.firstName || provider.name.split(' ')[0],
          lastName: provider?.lastName || provider.name.split(' ')[1],
          npi: provider?.npi,
          taxId: provider?.taxId,
        },
      },
    }
    try {
      const { error } = await addCheckEligibility(body)
      if (!error) {
        onOpenSnackbar('Eligibility added', 'success')
        // wait before redirect
        setTimeout(() => {
          goTo(`/v2/patient/${patient.id}/eligibility/show`, {
            modalType: 'show',
            eligibility,
            patient,
            onClaimClick,
          })
          setLoading(false)
        }, 3000)
      }
    } catch (error) {
      onOpenSnackbar(`${error}`, 'error')
    }
  }

  const statusColors = {
    failed: theme.palette.error.main,
    created: theme.palette.success.main,
    requested: theme.palette.warning.main,
    requested_with_error: theme.palette.error.main,
  }

  return (
    <Item theme={theme.palette}>
      <ContentWrapper
        onClick={() =>
          goTo(`/v2/patient/${patient.id}/eligibility/show`, {
            modalType: 'show',
            eligibility,
            patient,
            onClaimClick,
          })
        }
      >
        <SmallValue>
          <Typography fontSize="14px" sx={{ mb: '2px' }}>
            Created
          </Typography>
          <Typography bold fontSize="14px" sx={{ mb: '2px' }}>
            {safeFormat(parseISO(createdAt), 'MMMM dd, yyyy HH:mm')}
          </Typography>
        </SmallValue>
        <SmallValue>
          <Typography fontSize="14px" sx={{ mb: '2px' }}>
            Doctor
          </Typography>
          <Typography bold fontSize="14px" sx={{ mb: '2px' }}>
            {provider.firstName} {provider.lastName}
          </Typography>
        </SmallValue>
        <Value>
          <Typography fontSize="14px" sx={{ mb: '2px' }}>
            Insurance
          </Typography>
          <Typography bold fontSize="14px">
            {insurance?.payer?.name}
          </Typography>
        </Value>
        <Value></Value>
      </ContentWrapper>
      <ItemActions>
        <Button
          variant="contained"
          disableElevation
          size="small"
          onClick={() => {
            goTo(`/v2/patient/${patient.id}/eligibility/show`, {
              modalType: 'show',
              eligibility,
              patient,
              onClaimClick,
            })
          }}
          sx={{
            background: statusColors[status],
            color: theme.palette.background.default,
          }}
        >
          {status === 'requested_with_error' ? 'Check Errors' : status}
        </Button>
        <Button
          variant="outlined"
          disableElevation
          disabled={loading}
          loading={`${loading}`}
          onClick={() => handleCheckEligibility()}
          size="small"
          sx={{
            color: theme.palette.background.default,
            width: 'auto',
            minWidth: '40px',
          }}
        >
          <Icon>
            <SVGInline svg={ReloadIcon} />
          </Icon>
        </Button>
        <IconMenu
          options={options}
          icon={<MoreIcon fontSize="small" />}
          aria-label="More"
          title="More"
        />
      </ItemActions>

      <Dialog
        open={openDeleteConfirmation}
        onClose={() => setOpenDeleteConfirmation(false)}
      >
        <div style={{ padding: '38px 30px 23px 30px', textAlign: 'center' }}>
          <Typography variant="h3" sx={{ marginBottom: '24px' }}>
            Are you sure you want to remove this eligibility check?
          </Typography>
          <Typography
            variant="body2"
            sx={{ marginBottom: '24px', color: 'text.secondary' }}
          >
            This action cannot be undone. The eligibility information will be
            permanently removed.
          </Typography>
          <Grid
            display="flex"
            width={'100%'}
            justifyContent={'flex-end'}
            gap={1}
          >
            <Button
              variant="text"
              color="secondary"
              onClick={() => setOpenDeleteConfirmation(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDelete}
              sx={{
                color: '#fff',
              }}
            >
              Remove
            </Button>
          </Grid>
        </div>
      </Dialog>
    </Item>
  )
}

export default EligibilityInfo
